import React, { useEffect, useState } from "react";
import AppHeader from "../components/AppHeader";
import api, { API_BASE } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import {
  Users, Plus, Pencil, Trash2, Eye, EyeOff, Upload, FileText,
  ShieldCheck, ShieldAlert, ShieldX, BadgeCheck, Wallet, PiggyBank,
  CheckCircle2, XCircle, Search, Download, X, KeyRound, Building2, User, UserPlus,
} from "lucide-react";
import AddressAutocomplete from "../components/AddressAutocomplete";
import FooterCaixaBank from "../components/FooterCaixaBank";
import PasswordResetRequestsPanel from "../components/PasswordResetRequestsPanel";

const formatEUR = (n) =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n || 0));

const StatusPill = ({ status }) => {
  const map = {
    pending:  { label: "En attente", cls: "bg-amber-100 text-amber-800 border-amber-300", Icon: ShieldAlert },
    approved: { label: "Approuvé",   cls: "bg-emerald-100 text-emerald-800 border-emerald-300", Icon: BadgeCheck },
    blocked:  { label: "Bloqué",     cls: "bg-rose-100 text-rose-800 border-rose-300", Icon: ShieldX },
  };
  const it = map[status] || map.pending;
  const I = it.Icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 border text-[11px] font-semibold ${it.cls}`}>
      <I className="w-3 h-3" /> {it.label}
    </span>
  );
};

// ---------- Create / Edit Client modal ----------
const ClientFormModal = ({ open, onClose, onSaved, editClient, initialType = "particulier" }) => {
  const isEdit = !!editClient;
  const [form, setForm] = useState({
    email: "", password: "", first_name: "", last_name: "",
    account_type: "particulier", company_name: "", representatives: [],
    civility: "", co_civility: "", co_first_name: "", co_last_name: "",
    phone: "", address: "", credit_mobilier: 0, apport: 0, status: "pending",
    loan_amount: "", loan_duration: "", loan_rate: "", loan_monthly: "", 
    loan_contribution: "", loan_received: "",
    advisor_name: "", advisor_phone: "", advisor_email: "", advisor_address: "", advisor_photo: "",
    rib_iban: "", rib_bic: "", rib_bank_name: "", rib_holder: "",
  });
  const [saving, setSaving] = useState(false);
  const [currentPassword, setCurrentPassword] = useState(null);
  const [pwdAvailable, setPwdAvailable] = useState(false);
  const [pwdLoading, setPwdLoading] = useState(false);
  const [showCurrentPwd, setShowCurrentPwd] = useState(false);

  useEffect(() => {
    setShowCurrentPwd(false);
    if (isEdit && editClient?.id && open) {
      setPwdLoading(true);
      setCurrentPassword(null);
      setPwdAvailable(false);
      api.get(`/admin/clients/${editClient.id}/password`)
        .then((r) => {
          setCurrentPassword(r.data?.password ?? null);
          setPwdAvailable(!!r.data?.available);
        })
        .catch(() => { setCurrentPassword(null); setPwdAvailable(false); })
        .finally(() => setPwdLoading(false));
    } else {
      setCurrentPassword(null);
      setPwdAvailable(false);
      setPwdLoading(false);
    }
  }, [editClient, open, isEdit]);

  useEffect(() => {
    if (editClient) {
      setForm({
        email: editClient.email || "",
        password: "",
        account_type: editClient.account_type || "particulier",
        company_name: editClient.company_name || "",
        representatives: Array.isArray(editClient.representatives) ? editClient.representatives.map((r) => ({ civility: r.civility || "", first_name: r.first_name || "", last_name: r.last_name || "" })) : [],
        first_name: editClient.first_name || "",
        last_name: editClient.last_name || "",
        civility: editClient.civility || "",
        co_civility: editClient.co_civility || "",
        co_first_name: editClient.co_first_name || "",
        co_last_name: editClient.co_last_name || "",
        phone: editClient.phone || "",
        address: editClient.address || "",
        credit_mobilier: editClient.credit_mobilier || 0,
        apport: editClient.apport || 0,
        status: editClient.status || "pending",
        loan_amount: editClient.loan_amount || "",
        loan_duration: editClient.loan_duration || "",
        loan_rate: editClient.loan_rate || "",
        loan_monthly: editClient.loan_monthly || "",
        loan_contribution: editClient.loan_contribution || "",
        loan_received: editClient.loan_received || "",
        advisor_name: editClient.advisor_name || "",
        advisor_phone: editClient.advisor_phone || "",
        advisor_email: editClient.advisor_email || "",
        advisor_address: editClient.advisor_address || "",
        advisor_photo: editClient.advisor_photo || "",
        rib_iban: editClient.rib_iban || "",
        rib_bic: editClient.rib_bic || "",
        rib_bank_name: editClient.rib_bank_name || "",
        rib_holder: editClient.rib_holder || "",
      });
    } else {
      setForm({ 
        email: "", password: "", first_name: "", last_name: "", phone: "", 
        account_type: initialType || "particulier", company_name: "", representatives: [],
        civility: "", co_civility: "", co_first_name: "", co_last_name: "",
        address: "", credit_mobilier: 0, apport: 0, status: "pending",
        loan_amount: "", loan_duration: "", loan_rate: "", loan_monthly: "", 
        loan_contribution: "", loan_received: "",
        advisor_name: "", advisor_phone: "", advisor_email: "", advisor_address: "", advisor_photo: "",
        rib_iban: "", rib_bic: "", rib_bank_name: "", rib_holder: "",
      });
    }
  }, [editClient, open, initialType]);

  if (!open) return null;

  const isSci = form.account_type === "sci";

  // --- Représentants (SCI) ---
  const addRepresentative = () =>
    setForm((f) => ({ ...f, representatives: [...(f.representatives || []), { civility: "", first_name: "", last_name: "" }] }));
  const updateRepresentative = (idx, field, value) =>
    setForm((f) => {
      const reps = [...(f.representatives || [])];
      reps[idx] = { ...reps[idx], [field]: value };
      return { ...f, representatives: reps };
    });
  const removeRepresentative = (idx) =>
    setForm((f) => ({ ...f, representatives: (f.representatives || []).filter((_, i) => i !== idx) }));

  // Téléverser + compresser la photo du conseiller (vignette ronde ~256px)
  const handleAdvisorPhoto = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Veuillez choisir une image."); return; }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const size = 256;
        const canvas = document.createElement("canvas");
        canvas.width = size; canvas.height = size;
        const ctx = canvas.getContext("2d");
        // Recadrage centré (cover)
        const scale = Math.max(size / img.width, size / img.height);
        const w = img.width * scale, h = img.height * scale;
        ctx.drawImage(img, (size - w) / 2, (size - h) / 2, w, h);
        const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
        setForm((f) => ({ ...f, advisor_photo: dataUrl }));
      };
      img.onerror = () => toast.error("Image invalide.");
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  };

  // Champs numériques : ils doivent être envoyés comme nombres (ou null),
  // jamais comme chaînes vides sinon la validation backend échoue (422).
  const NUMERIC_FIELDS = [
    "credit_mobilier", "apport", "loan_amount", "loan_duration", "loan_rate",
    "loan_monthly", "loan_contribution", "loan_received",
  ];
  const REQUIRED_NUMERIC = ["credit_mobilier", "apport"];
  const INTEGER_FIELDS = ["loan_duration"];

  // Convertit une saisie utilisateur (virgule OU point, espaces) en nombre.
  // Ex: "19000,43" -> 19000.43 ; "19 000,43" -> 19000.43
  const toNumber = (v) => {
    if (typeof v === "string") {
      const cleaned = v.replace(/\s/g, "").replace(",", ".");
      if (cleaned === "") return NaN;
      return Number(cleaned);
    }
    return Number(v);
  };

  const buildPayload = (src) => {
    const p = { ...src };
    NUMERIC_FIELDS.forEach((k) => {
      const v = p[k];
      if (v === "" || v === null || v === undefined) {
        p[k] = REQUIRED_NUMERIC.includes(k) ? 0 : null;
      } else {
        let n = toNumber(v);
        if (Number.isNaN(n)) {
          p[k] = REQUIRED_NUMERIC.includes(k) ? 0 : null;
        } else {
          if (INTEGER_FIELDS.includes(k)) n = Math.round(n);
          p[k] = n;
        }
      }
    });
    // Nettoyage des représentants (SCI) : on retire les lignes totalement vides
    if (Array.isArray(p.representatives)) {
      p.representatives = p.representatives
        .map((r) => ({ civility: (r.civility || "").trim(), first_name: (r.first_name || "").trim(), last_name: (r.last_name || "").trim() }))
        .filter((r) => r.civility || r.first_name || r.last_name);
    }
    return p;
  };

  const extractError = (err) => {
    const detail = err?.response?.data?.detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) {
      const msg = detail.map((d) => d?.msg).filter(Boolean).join(", ");
      if (msg) return msg;
    }
    return "Erreur lors de l'enregistrement.";
  };

  const save = async (e) => {
    e.preventDefault();
    if (isSci && !form.company_name.trim()) {
      toast.error("Le nom de la SCI est requis.");
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        const payload = buildPayload(form);
        if (!payload.password) delete payload.password;
        delete payload.email; // email not updatable
        await api.put(`/admin/clients/${editClient.id}`, payload);
        toast.success("Client mis à jour.");
      } else {
        await api.post("/admin/clients", buildPayload(form));
        toast.success("Client créé.");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-3" onClick={onClose}>
      <form
        onSubmit={save}
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full max-w-2xl max-h-[92vh] overflow-y-auto"
      >
        <div className="sticky top-0 bg-[#007EAE] text-white px-5 py-3 flex items-center justify-between">
          <h3 className="font-semibold text-lg">{isEdit ? "Modifier le client" : "Nouveau client"} — {isSci ? "Société (SCI)" : "Particulier"}</h3>
          <button type="button" onClick={onClose} className="text-white/90 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-[12.5px] font-medium text-slate-700">E-mail</label>
            <input required type="email" value={form.email} disabled={isEdit}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full mt-1 px-3 py-2.5 border border-slate-300 focus:border-[#007EAE] focus:ring-2 focus:ring-[#007EAE]/20 outline-none text-[15px] rounded-none disabled:bg-slate-50 disabled:text-slate-500" />
          </div>
          <div>
            <label className="text-[12.5px] font-medium text-slate-700">{isEdit ? "Nouveau mot de passe (optionnel)" : "Mot de passe"}</label>
            <input type="text" required={!isEdit} value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="w-full mt-1 px-3 py-2.5 border border-slate-300 focus:border-[#007EAE] focus:ring-2 focus:ring-[#007EAE]/20 outline-none text-[15px] rounded-none" />
          </div>
          {isEdit && (
            <div className="sm:col-span-2">
              <label className="text-[12.5px] font-medium text-slate-700">Mot de passe actuel</label>
              {pwdLoading ? (
                <div className="w-full mt-1 px-3 py-2.5 border border-slate-200 bg-slate-50 text-slate-400 text-[15px]">Chargement…</div>
              ) : pwdAvailable ? (
                <div className="relative mt-1">
                  <input type={showCurrentPwd ? "text" : "password"} readOnly value={currentPassword || ""}
                    className="w-full px-3 py-2.5 pr-11 border border-slate-300 bg-slate-50 text-slate-800 outline-none text-[15px] rounded-none font-mono" />
                  <button type="button" onClick={() => setShowCurrentPwd((v) => !v)}
                    title={showCurrentPwd ? "Masquer" : "Afficher"}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-slate-500 hover:text-slate-700">
                    {showCurrentPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              ) : (
                <div className="w-full mt-1 px-3 py-2.5 border border-slate-200 bg-slate-50 text-slate-500 text-[13px]">
                  Non disponible pour ce compte (mot de passe défini avant l'activation de cette fonctionnalité). Il apparaîtra dès le prochain changement de mot de passe.
                </div>
              )}
            </div>
          )}

          {isSci && (
            <div className="sm:col-span-2">
              <label className="text-[12.5px] font-medium text-slate-700">Nom de la SCI (raison sociale)</label>
              <input required value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                placeholder="Ex: SCI LOU PIER"
                className="w-full mt-1 px-3 py-2.5 border border-slate-300 focus:border-[#007EAE] focus:ring-2 focus:ring-[#007EAE]/20 outline-none text-[15px] rounded-none" />
            </div>
          )}

          {isSci && (
            <div className="sm:col-span-2 pt-4 border-t border-slate-200">
              <div className="flex items-center justify-between mb-1">
                <h4 className="text-sm font-semibold text-[#007EAE]">Représentants légaux de la SCI</h4>
                <button type="button" onClick={addRepresentative}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#007EAE] text-[#007EAE] hover:bg-[#007EAE]/5 text-[13px] font-semibold">
                  <UserPlus className="w-4 h-4" /> Ajouter
                </button>
              </div>
              <p className="text-[12px] text-slate-500 mb-3">Ajoutez une ou plusieurs personnes qui représentent la SCI.</p>
              {(form.representatives || []).length === 0 && (
                <p className="text-[13px] text-slate-400 mb-2">Aucun représentant ajouté pour le moment.</p>
              )}
              <div className="space-y-3">
                {(form.representatives || []).map((rep, idx) => (
                  <div key={idx} className="p-3 border border-slate-200 bg-slate-50">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[12px] font-semibold text-slate-600">Représentant {idx + 1}</span>
                      <button type="button" onClick={() => removeRepresentative(idx)} title="Retirer"
                        className="p-1.5 border border-rose-300 text-rose-600 hover:bg-rose-50"><Trash2 className="w-4 h-4" /></button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div>
                        <label className="text-[12px] font-medium text-slate-700">Civilité</label>
                        <select value={rep.civility || ""} onChange={(e) => updateRepresentative(idx, "civility", e.target.value)}
                          className="w-full mt-1 px-3 py-2.5 border border-slate-300 focus:border-[#007EAE] focus:ring-2 focus:ring-[#007EAE]/20 outline-none text-[15px] rounded-none bg-white">
                          <option value="">—</option>
                          <option value="Monsieur">Monsieur</option>
                          <option value="Madame">Madame</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-[12px] font-medium text-slate-700">Prénom</label>
                        <input value={rep.first_name || ""} onChange={(e) => updateRepresentative(idx, "first_name", e.target.value)}
                          className="w-full mt-1 px-3 py-2.5 border border-slate-300 focus:border-[#007EAE] focus:ring-2 focus:ring-[#007EAE]/20 outline-none text-[15px] rounded-none" />
                      </div>
                      <div>
                        <label className="text-[12px] font-medium text-slate-700">Nom</label>
                        <input value={rep.last_name || ""} onChange={(e) => updateRepresentative(idx, "last_name", e.target.value)}
                          className="w-full mt-1 px-3 py-2.5 border border-slate-300 focus:border-[#007EAE] focus:ring-2 focus:ring-[#007EAE]/20 outline-none text-[15px] rounded-none" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {!isSci && (<>
          <div className="sm:col-span-2">
            <label className="text-[12.5px] font-medium text-slate-700">Civilité</label>
            <select value={form.civility} onChange={(e) => setForm({ ...form, civility: e.target.value })}
              className="w-full mt-1 px-3 py-2.5 border border-slate-300 focus:border-[#007EAE] focus:ring-2 focus:ring-[#007EAE]/20 outline-none text-[15px] rounded-none bg-white">
              <option value="">— Sélectionner —</option>
              <option value="Monsieur">Monsieur</option>
              <option value="Madame">Madame</option>
            </select>
          </div>
          <div>
            <label className="text-[12.5px] font-medium text-slate-700">Prénom</label>
            <input required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })}
              className="w-full mt-1 px-3 py-2.5 border border-slate-300 focus:border-[#007EAE] focus:ring-2 focus:ring-[#007EAE]/20 outline-none text-[15px] rounded-none" />
          </div>
          <div>
            <label className="text-[12.5px] font-medium text-slate-700">Nom</label>
            <input required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })}
              className="w-full mt-1 px-3 py-2.5 border border-slate-300 focus:border-[#007EAE] focus:ring-2 focus:ring-[#007EAE]/20 outline-none text-[15px] rounded-none" />
          </div>

          {/* SECTION: Second titulaire (optionnel) */}
          <div className="sm:col-span-2 pt-4 border-t border-slate-200">
            <h4 className="text-sm font-semibold text-[#007EAE] mb-1">Second titulaire (optionnel)</h4>
            <p className="text-[12px] text-slate-500 mb-3">À remplir uniquement pour un compte joint (Monsieur et Madame).</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="text-[12.5px] font-medium text-slate-700">Civilité</label>
                <select value={form.co_civility} onChange={(e) => setForm({ ...form, co_civility: e.target.value })}
                  className="w-full mt-1 px-3 py-2.5 border border-slate-300 focus:border-[#007EAE] focus:ring-2 focus:ring-[#007EAE]/20 outline-none text-[15px] rounded-none bg-white">
                  <option value="">— Aucun —</option>
                  <option value="Monsieur">Monsieur</option>
                  <option value="Madame">Madame</option>
                </select>
              </div>
              <div>
                <label className="text-[12.5px] font-medium text-slate-700">Prénom (2ᵉ titulaire)</label>
                <input value={form.co_first_name} onChange={(e) => setForm({ ...form, co_first_name: e.target.value })}
                  className="w-full mt-1 px-3 py-2.5 border border-slate-300 focus:border-[#007EAE] focus:ring-2 focus:ring-[#007EAE]/20 outline-none text-[15px] rounded-none" />
              </div>
              <div>
                <label className="text-[12.5px] font-medium text-slate-700">Nom (2ᵉ titulaire)</label>
                <input value={form.co_last_name} onChange={(e) => setForm({ ...form, co_last_name: e.target.value })}
                  className="w-full mt-1 px-3 py-2.5 border border-slate-300 focus:border-[#007EAE] focus:ring-2 focus:ring-[#007EAE]/20 outline-none text-[15px] rounded-none" />
              </div>
            </div>
          </div>
          </>)}
          <div>
            <label className="text-[12.5px] font-medium text-slate-700">Téléphone</label>
            <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="w-full mt-1 px-3 py-2.5 border border-slate-300 focus:border-[#007EAE] focus:ring-2 focus:ring-[#007EAE]/20 outline-none text-[15px] rounded-none" />
          </div>
          <div>
            <label className="text-[12.5px] font-medium text-slate-700">Statut du compte</label>
            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
              className="w-full mt-1 px-3 py-2.5 border border-slate-300 focus:border-[#007EAE] focus:ring-2 focus:ring-[#007EAE]/20 outline-none text-[15px] rounded-none bg-white">
              <option value="pending">En attente</option>
              <option value="approved">Approuvé</option>
              <option value="blocked">Bloqué</option>
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="text-[12.5px] font-medium text-slate-700">Adresse</label>
            <div className="mt-1">
              <AddressAutocomplete value={form.address} onChange={(v) => setForm({ ...form, address: v })} placeholder="Commencez à taper l'adresse…" />
            </div>
          </div>
          <div>
            <label className="text-[12.5px] font-medium text-slate-700">Crédit mobilier (€)</label>
            <input type="text" inputMode="decimal" value={form.credit_mobilier}
              onChange={(e) => setForm({ ...form, credit_mobilier: e.target.value })}
              className="w-full mt-1 px-3 py-2.5 border border-slate-300 focus:border-[#007EAE] focus:ring-2 focus:ring-[#007EAE]/20 outline-none text-[15px] rounded-none" />
          </div>
          <div>
            <label className="text-[12.5px] font-medium text-slate-700">Apport (€)</label>
            <input type="text" inputMode="decimal" value={form.apport}
              onChange={(e) => setForm({ ...form, apport: e.target.value })}
              className="w-full mt-1 px-3 py-2.5 border border-slate-300 focus:border-[#007EAE] focus:ring-2 focus:ring-[#007EAE]/20 outline-none text-[15px] rounded-none" />
          </div>

          {/* SECTION: Détails du prêt */}
          <div className="sm:col-span-2 pt-4 border-t border-slate-200">
            <h4 className="text-sm font-semibold text-[#007EAE] mb-3">Détails du prêt</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-[12.5px] font-medium text-slate-700">Montant du prêt (€)</label>
                <input type="text" inputMode="decimal" value={form.loan_amount}
                  onChange={(e) => setForm({ ...form, loan_amount: e.target.value })}
                  className="w-full mt-1 px-3 py-2.5 border border-slate-300 focus:border-[#007EAE] focus:ring-2 focus:ring-[#007EAE]/20 outline-none text-[15px] rounded-none" />
              </div>
              <div>
                <label className="text-[12.5px] font-medium text-slate-700">Durée (années)</label>
                <input type="text" inputMode="numeric" value={form.loan_duration}
                  onChange={(e) => setForm({ ...form, loan_duration: e.target.value })}
                  className="w-full mt-1 px-3 py-2.5 border border-slate-300 focus:border-[#007EAE] focus:ring-2 focus:ring-[#007EAE]/20 outline-none text-[15px] rounded-none" />
              </div>
              <div>
                <label className="text-[12.5px] font-medium text-slate-700">Taux d'intérêt (%)</label>
                <input type="text" inputMode="decimal" value={form.loan_rate}
                  onChange={(e) => setForm({ ...form, loan_rate: e.target.value })}
                  className="w-full mt-1 px-3 py-2.5 border border-slate-300 focus:border-[#007EAE] focus:ring-2 focus:ring-[#007EAE]/20 outline-none text-[15px] rounded-none" />
              </div>
              <div>
                <label className="text-[12.5px] font-medium text-slate-700">Mensualité (€)</label>
                <input type="text" inputMode="decimal" value={form.loan_monthly}
                  onChange={(e) => setForm({ ...form, loan_monthly: e.target.value })}
                  className="w-full mt-1 px-3 py-2.5 border border-slate-300 focus:border-[#007EAE] focus:ring-2 focus:ring-[#007EAE]/20 outline-none text-[15px] rounded-none" />
              </div>
              <div>
                <label className="text-[12.5px] font-medium text-slate-700">Apport personnel (€)</label>
                <input type="text" inputMode="decimal" value={form.loan_contribution}
                  onChange={(e) => setForm({ ...form, loan_contribution: e.target.value })}
                  className="w-full mt-1 px-3 py-2.5 border border-slate-300 focus:border-[#007EAE] focus:ring-2 focus:ring-[#007EAE]/20 outline-none text-[15px] rounded-none" />
              </div>
              <div>
                <label className="text-[12.5px] font-medium text-slate-700">Versement reçu (€)</label>
                <input type="text" inputMode="decimal" value={form.loan_received}
                  onChange={(e) => setForm({ ...form, loan_received: e.target.value })}
                  className="w-full mt-1 px-3 py-2.5 border border-slate-300 focus:border-[#007EAE] focus:ring-2 focus:ring-[#007EAE]/20 outline-none text-[15px] rounded-none" />
                <p className="text-[11px] text-slate-500 mt-1">Montant déjà versé par le client sur son apport personnel</p>
              </div>
            </div>
          </div>

          {/* SECTION: Conseiller */}
          <div className="sm:col-span-2 pt-4 border-t border-slate-200">
            <h4 className="text-sm font-semibold text-[#007EAE] mb-3">Informations du conseiller</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-[12.5px] font-medium text-slate-700">Nom du conseiller</label>
                <input type="text" value={form.advisor_name}
                  onChange={(e) => setForm({ ...form, advisor_name: e.target.value })}
                  className="w-full mt-1 px-3 py-2.5 border border-slate-300 focus:border-[#007EAE] focus:ring-2 focus:ring-[#007EAE]/20 outline-none text-[15px] rounded-none" />
              </div>
              <div>
                <label className="text-[12.5px] font-medium text-slate-700">Téléphone du conseiller</label>
                <input type="text" value={form.advisor_phone}
                  onChange={(e) => setForm({ ...form, advisor_phone: e.target.value })}
                  className="w-full mt-1 px-3 py-2.5 border border-slate-300 focus:border-[#007EAE] focus:ring-2 focus:ring-[#007EAE]/20 outline-none text-[15px] rounded-none" />
              </div>
              <div>
                <label className="text-[12.5px] font-medium text-slate-700">Email du conseiller</label>
                <input type="email" value={form.advisor_email}
                  onChange={(e) => setForm({ ...form, advisor_email: e.target.value })}
                  className="w-full mt-1 px-3 py-2.5 border border-slate-300 focus:border-[#007EAE] focus:ring-2 focus:ring-[#007EAE]/20 outline-none text-[15px] rounded-none" />
              </div>
              <div>
                <label className="text-[12.5px] font-medium text-slate-700">Adresse de l'agence</label>
                <input type="text" value={form.advisor_address}
                  onChange={(e) => setForm({ ...form, advisor_address: e.target.value })}
                  className="w-full mt-1 px-3 py-2.5 border border-slate-300 focus:border-[#007EAE] focus:ring-2 focus:ring-[#007EAE]/20 outline-none text-[15px] rounded-none" />
              </div>
              <div className="sm:col-span-2">
                <label className="text-[12.5px] font-medium text-slate-700">Photo du conseiller</label>
                <div className="mt-1 flex items-center gap-3">
                  <div className="w-16 h-16 rounded-full overflow-hidden bg-[#007EAE]/10 flex items-center justify-center shrink-0 border border-slate-200">
                    {form.advisor_photo ? (
                      <img src={form.advisor_photo} alt="Conseiller" className="w-full h-full object-cover" />
                    ) : (
                      <User className="w-7 h-7 text-[#007EAE]" />
                    )}
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <input type="file" accept="image/*" onChange={(e) => handleAdvisorPhoto(e.target.files?.[0])}
                      className="text-sm file:mr-3 file:py-2 file:px-3 file:border-0 file:bg-[#007EAE] file:text-white file:font-semibold" />
                    {form.advisor_photo && (
                      <button type="button" onClick={() => setForm((f) => ({ ...f, advisor_photo: "" }))}
                        className="self-start text-[12px] text-rose-600 hover:underline">Retirer la photo</button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* SECTION: Configuration du RIB */}
          <div className="sm:col-span-2 pt-4 border-t border-slate-200">
            <h4 className="text-sm font-semibold text-[#007EAE] mb-3">Configuration du RIB</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-[12.5px] font-medium text-slate-700">IBAN</label>
                <input type="text" value={form.rib_iban}
                  onChange={(e) => setForm({ ...form, rib_iban: e.target.value })}
                  placeholder="FR76 3083 3830 0005 6971 14"
                  className="w-full mt-1 px-3 py-2.5 border border-slate-300 focus:border-[#007EAE] focus:ring-2 focus:ring-[#007EAE]/20 outline-none text-[15px] rounded-none" />
              </div>
              <div>
                <label className="text-[12.5px] font-medium text-slate-700">BIC</label>
                <input type="text" value={form.rib_bic}
                  onChange={(e) => setForm({ ...form, rib_bic: e.target.value })}
                  placeholder="FNOMFRP2"
                  className="w-full mt-1 px-3 py-2.5 border border-slate-300 focus:border-[#007EAE] focus:ring-2 focus:ring-[#007EAE]/20 outline-none text-[15px] rounded-none" />
              </div>
              <div>
                <label className="text-[12.5px] font-medium text-slate-700">Nom de la banque</label>
                <input type="text" value={form.rib_bank_name}
                  onChange={(e) => setForm({ ...form, rib_bank_name: e.target.value })}
                  className="w-full mt-1 px-3 py-2.5 border border-slate-300 focus:border-[#007EAE] focus:ring-2 focus:ring-[#007EAE]/20 outline-none text-[15px] rounded-none" />
              </div>
              <div>
                <label className="text-[12.5px] font-medium text-slate-700">Titulaire du compte</label>
                <input type="text" value={form.rib_holder}
                  onChange={(e) => setForm({ ...form, rib_holder: e.target.value })}
                  className="w-full mt-1 px-3 py-2.5 border border-slate-300 focus:border-[#007EAE] focus:ring-2 focus:ring-[#007EAE]/20 outline-none text-[15px] rounded-none" />
              </div>
            </div>
          </div>
        </div>
        <div className="px-5 py-4 bg-slate-50 border-t border-slate-200 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 bg-white border border-slate-300 hover:bg-slate-100 text-slate-700 font-semibold">Annuler</button>
          <button disabled={saving} type="submit" className="px-5 py-2 bg-[#007EAE] hover:bg-[#006c97] text-white font-semibold disabled:opacity-70">
            {saving ? "Enregistrement…" : isEdit ? "Mettre à jour" : "Créer le client"}
          </button>
        </div>
      </form>
    </div>
  );
};

// ---------- Offers panel ----------
const OffersPanel = ({ client, onClose }) => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState("");
  const [visible, setVisible] = useState(true);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [rib, setRib] = useState(null);
  const [ribFile, setRibFile] = useState(null);
  const [ribUploading, setRibUploading] = useState(false);

  const fetchRib = async () => {
    try {
      const r = await api.get(`/admin/clients/${client.id}/rib`);
      setRib(r.data || null);
    } catch { setRib(null); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/clients/${client.id}/offers`);
      setOffers(r.data || []);
      await fetchRib();
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [client.id]); // eslint-disable-line

  const upload = async (e) => {
    e.preventDefault();
    if (!file || !title) { toast.error("Titre et fichier requis."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("title", title);
      fd.append("visible", visible ? "true" : "false");
      fd.append("file", file);
      await api.post(`/admin/clients/${client.id}/offers`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setTitle(""); setFile(null); setVisible(true);
      toast.success("Offre téléversée.");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur de téléversement.");
    } finally { setUploading(false); }
  };

  const toggleVisible = async (o) => {
    try {
      await api.put(`/admin/offers/${o.id}`, { visible: !o.visible });
      load();
    } catch { toast.error("Erreur."); }
  };

  const remove = async (o) => {
    if (!window.confirm("Supprimer cette offre ?")) return;
    try {
      await api.delete(`/admin/offers/${o.id}`);
      load();
      toast.success("Offre supprimée.");
    } catch { toast.error("Erreur."); }
  };

  const download = async (o) => {
    try {
      // Récupérer le token de la session active de cet onglet
      const currentSessionEmail = sessionStorage.getItem("hb_current_session");
      const tokenKey = currentSessionEmail ? `hb_token_${currentSessionEmail}` : "hb_token";
      const token = localStorage.getItem(tokenKey);
      
      const res = await fetch(`${API_BASE}/offers/${o.id}/download`, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await res.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = o.file_name || "offre.pdf";
      link.click();
      URL.revokeObjectURL(link.href);
    } catch { toast.error("Erreur de téléchargement."); }
  };

  const uploadRib = async (e) => {
    e.preventDefault();
    if (!ribFile) { toast.error("Veuillez choisir un fichier PDF."); return; }
    setRibUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", ribFile);
      await api.post(`/admin/clients/${client.id}/rib`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setRibFile(null);
      toast.success("RIB téléversé.");
      await fetchRib();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur de téléversement du RIB.");
    } finally { setRibUploading(false); }
  };

  const deleteRib = async () => {
    if (!window.confirm("Supprimer le RIB de ce client ?")) return;
    try {
      await api.delete(`/admin/clients/${client.id}/rib`);
      toast.success("RIB supprimé.");
      await fetchRib();
    } catch { toast.error("Erreur."); }
  };

  const getRibBlob = async () => {
    const currentSessionEmail = sessionStorage.getItem("hb_current_session");
    const tokenKey = currentSessionEmail ? `hb_token_${currentSessionEmail}` : "hb_token";
    const token = localStorage.getItem(tokenKey);
    const res = await fetch(`${API_BASE}/rib/${client.id}/download`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) throw new Error();
    return res.blob();
  };

  const viewRib = async () => {
    try {
      const blob = await getRibBlob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Impossible d'ouvrir le RIB."); }
  };

  const downloadRib = async () => {
    try {
      const blob = await getRibBlob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = rib?.file_name || "rib.pdf";
      link.click();
      URL.revokeObjectURL(link.href);
    } catch { toast.error("Erreur de téléchargement."); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-3" onClick={onClose}>
      <div className="bg-white w-full max-w-3xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-[#007EAE] text-white px-5 py-3 flex items-center justify-between">
          <h3 className="font-semibold text-lg">Offres — {client.first_name} {client.last_name}</h3>
          <button type="button" onClick={onClose} className="text-white/90 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <form onSubmit={upload} className="p-5 grid grid-cols-1 sm:grid-cols-12 gap-3 items-end border-b border-slate-200 bg-slate-50">
          <div className="sm:col-span-5">
            <label className="text-[12px] font-medium text-slate-700">Titre de l'offre</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ex: Offre de prêt n°1"
              className="w-full mt-1 px-3 py-2.5 border border-slate-300 focus:border-[#007EAE] focus:ring-2 focus:ring-[#007EAE]/20 outline-none text-sm rounded-none" />
          </div>
          <div className="sm:col-span-5">
            <label className="text-[12px] font-medium text-slate-700">Fichier PDF</label>
            <input type="file" accept="application/pdf,.pdf" onChange={(e) => setFile(e.target.files?.[0])}
              className="w-full mt-1 text-sm file:mr-3 file:py-2 file:px-3 file:border-0 file:bg-[#007EAE] file:text-white file:font-semibold" />
          </div>
          <label className="sm:col-span-2 inline-flex items-center gap-2 text-sm select-none">
            <input type="checkbox" checked={visible} onChange={(e) => setVisible(e.target.checked)} />
            Visible client
          </label>
          <div className="sm:col-span-12">
            <button disabled={uploading} type="submit"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#007EAE] hover:bg-[#006c97] text-white font-semibold disabled:opacity-70">
              <Upload className="w-4 h-4" /> {uploading ? "Téléversement…" : "Téléverser l'offre"}
            </button>
          </div>
        </form>

        {/* SECTION RIB (PDF) */}
        <div className="p-5 border-b border-slate-200 bg-white">
          <h4 className="text-sm font-semibold text-[#007EAE] mb-3">RIB (PDF)</h4>
          {rib ? (
            <div className="p-3 border border-slate-200 flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between mb-3">
              <div className="flex items-center gap-3 min-w-0">
                <FileText className="w-5 h-5 text-[#007EAE] shrink-0" />
                <div className="min-w-0">
                  <p className="font-semibold text-slate-800 truncate">{rib.file_name}</p>
                  <p className="text-[12px] text-slate-500 truncate">{(rib.size / 1024).toFixed(1)} Ko</p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <button onClick={viewRib} title="Visionner" type="button"
                  className="p-2 border border-slate-300 hover:bg-slate-100"><Eye className="w-4 h-4" /></button>
                <button onClick={downloadRib} title="Télécharger" type="button"
                  className="p-2 border border-slate-300 hover:bg-slate-100"><Download className="w-4 h-4" /></button>
                <button onClick={deleteRib} title="Supprimer" type="button"
                  className="p-2 border border-rose-300 text-rose-600 hover:bg-rose-50"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ) : (
            <p className="text-slate-500 text-sm mb-3">Aucun RIB (PDF) pour ce client.</p>
          )}
          <form onSubmit={uploadRib} className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-end">
            <div className="sm:col-span-8">
              <label className="text-[12px] font-medium text-slate-700">Fichier PDF du RIB</label>
              <input type="file" accept="application/pdf,.pdf" onChange={(e) => setRibFile(e.target.files?.[0])}
                className="w-full mt-1 text-sm file:mr-3 file:py-2 file:px-3 file:border-0 file:bg-[#007EAE] file:text-white file:font-semibold" />
            </div>
            <div className="sm:col-span-4">
              <button disabled={ribUploading} type="submit"
                className="w-full sm:w-auto inline-flex items-center gap-2 px-5 py-2.5 bg-[#007EAE] hover:bg-[#006c97] text-white font-semibold disabled:opacity-70">
                <Upload className="w-4 h-4" /> {ribUploading ? "Téléversement…" : (rib ? "Remplacer le RIB" : "Téléverser le RIB")}
              </button>
            </div>
          </form>
        </div>

        <div className="p-5">
          {loading ? <p className="text-slate-500 text-sm">Chargement…</p> :
            offers.length === 0 ? <p className="text-slate-500 text-sm">Aucune offre pour ce client.</p> : (
            <ul className="space-y-2">
              {offers.map((o) => (
                <li key={o.id} className="p-3 border border-slate-200 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-2 sm:justify-between">
                  <div className="flex items-center gap-3 min-w-0">
                    <FileText className="w-5 h-5 text-[#007EAE] shrink-0" />
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-800 truncate">{o.title}</p>
                      <p className="text-[12px] text-slate-500 truncate">{o.file_name} • {(o.size/1024).toFixed(1)} Ko</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold border ${o.visible ? "bg-emerald-50 border-emerald-300 text-emerald-700" : "bg-slate-100 border-slate-300 text-slate-600"}`}>
                      {o.visible ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                      {o.visible ? "Visible" : "Masqué"}
                    </span>
                    <button onClick={() => toggleVisible(o)} title={o.visible ? "Masquer" : "Afficher"}
                      className="p-2 border border-slate-300 hover:bg-slate-100" type="button">
                      {o.visible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                    <button onClick={() => download(o)} title="Télécharger"
                      className="p-2 border border-slate-300 hover:bg-slate-100" type="button"><Download className="w-4 h-4" /></button>
                    <button onClick={() => remove(o)} title="Supprimer"
                      className="p-2 border border-rose-300 text-rose-600 hover:bg-rose-50" type="button"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

// ---------- Main page ----------
const AdminDashboard = () => {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [editClient, setEditClient] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [newClientType, setNewClientType] = useState("particulier");
  const [typeChooserOpen, setTypeChooserOpen] = useState(false);
  const [offersClient, setOffersClient] = useState(null);
  const [resetRequestsOpen, setResetRequestsOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/clients");
      setClients(r.data || []);
    } catch (e) {
      toast.error("Erreur de chargement des clients.");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const quickStatus = async (c, status) => {
    try {
      await api.put(`/admin/clients/${c.id}`, { status });
      toast.success(
        status === "approved" ? "Compte approuvé." :
        status === "blocked"  ? "Compte bloqué." : "Statut mis à jour."
      );
      load();
    } catch { toast.error("Erreur."); }
  };

  const removeClient = async (c) => {
    if (!window.confirm(`Supprimer définitivement ${c.email} ?`)) return;
    try {
      await api.delete(`/admin/clients/${c.id}`);
      toast.success("Client supprimé.");
      load();
    } catch { toast.error("Erreur."); }
  };

  const filtered = clients.filter((c) => {
    const s = q.toLowerCase().trim();
    if (!s) return true;
    return (
      c.email.toLowerCase().includes(s) ||
      (c.first_name || "").toLowerCase().includes(s) ||
      (c.last_name || "").toLowerCase().includes(s)
    );
  });

  if (!user) return null;

  const totals = clients.reduce((acc, c) => ({
    credit: acc.credit + Number(c.credit_mobilier || 0),
    apport: acc.apport + Number(c.apport || 0),
    approved: acc.approved + (c.status === "approved" ? 1 : 0),
    pending: acc.pending + (c.status === "pending" ? 1 : 0),
  }), { credit: 0, apport: 0, approved: 0, pending: 0 });

  return (
    <div className="min-h-screen bg-slate-50">
      <AppHeader />
      <main className="max-w-[1280px] mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-slate-800" style={{ fontFamily: "Georgia, serif" }}>Espace administrateur</h1>
            <p className="text-sm text-slate-500">Gérez les clients, leurs montants et leurs offres.</p>
          </div>
          <div className="flex gap-2">
            <button 
              onClick={() => setResetRequestsOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-slate-700 hover:bg-slate-800 text-white font-semibold btn-press">
              <KeyRound className="w-4 h-4" /> Demandes de récupération
            </button>
            <button onClick={() => { setEditClient(null); setTypeChooserOpen(true); }}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#007EAE] hover:bg-[#006c97] text-white font-semibold btn-press">
              <Plus className="w-4 h-4" /> Nouveau client
            </button>
          </div>
        </div>

        {/* KPIs */}
        <div className="mt-5 grid grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="p-4 bg-white border border-slate-200">
            <div className="flex items-center gap-2 text-slate-500 text-xs"><Users className="w-4 h-4" /> Clients</div>
            <p className="text-2xl font-bold text-[#003E7E] mt-1">{clients.length}</p>
          </div>
          <div className="p-4 bg-white border border-slate-200">
            <div className="flex items-center gap-2 text-slate-500 text-xs"><BadgeCheck className="w-4 h-4" /> Approuvés / En attente</div>
            <p className="text-2xl font-bold text-[#003E7E] mt-1">{totals.approved} <span className="text-slate-400 text-base">/ {totals.pending}</span></p>
          </div>
          <div className="p-4 bg-white border border-slate-200">
            <div className="flex items-center gap-2 text-slate-500 text-xs"><Wallet className="w-4 h-4" /> Total crédit mobilier</div>
            <p className="text-2xl font-bold text-[#003E7E] mt-1">{formatEUR(totals.credit)}</p>
          </div>
          <div className="p-4 bg-white border border-slate-200">
            <div className="flex items-center gap-2 text-slate-500 text-xs"><PiggyBank className="w-4 h-4" /> Total apports</div>
            <p className="text-2xl font-bold text-[#003E7E] mt-1">{formatEUR(totals.apport)}</p>
          </div>
        </div>

        {/* Search */}
        <div className="mt-6 relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un client (nom, e-mail…)"
            className="w-full sm:max-w-md pl-9 pr-3 py-2.5 border border-slate-300 focus:border-[#007EAE] focus:ring-2 focus:ring-[#007EAE]/20 outline-none text-sm rounded-none bg-white" />
        </div>

        {/* Table */}
        <div className="mt-4 bg-white border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-slate-600">
                <th className="px-4 py-3 font-semibold">Client</th>
                <th className="px-4 py-3 font-semibold">Statut</th>
                <th className="px-4 py-3 font-semibold text-right">Crédit mobilier</th>
                <th className="px-4 py-3 font-semibold text-right">Apport</th>
                <th className="px-4 py-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (<tr><td colSpan="5" className="px-4 py-10 text-center text-slate-500">Chargement…</td></tr>)}
              {!loading && filtered.length === 0 && (<tr><td colSpan="5" className="px-4 py-10 text-center text-slate-500">Aucun client.</td></tr>)}
              {filtered.map((c) => (
                <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50/60">
                  <td className="px-4 py-3">
                    <p className="font-semibold text-slate-800">{c.first_name} {c.last_name}</p>
                    <p className="text-[12px] text-slate-500">{c.email}</p>
                  </td>
                  <td className="px-4 py-3"><StatusPill status={c.status} /></td>
                  <td className="px-4 py-3 text-right text-slate-800 whitespace-nowrap">{formatEUR(c.credit_mobilier)}</td>
                  <td className="px-4 py-3 text-right text-slate-800 whitespace-nowrap">{formatEUR(c.apport)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5 justify-end flex-wrap">
                      {c.status !== "approved" && (
                        <button onClick={() => quickStatus(c, "approved")} title="Approuver" className="p-2 border border-emerald-300 text-emerald-700 hover:bg-emerald-50"><CheckCircle2 className="w-4 h-4" /></button>
                      )}
                      {c.status !== "blocked" ? (
                        <button onClick={() => quickStatus(c, "blocked")} title="Bloquer" className="p-2 border border-rose-300 text-rose-700 hover:bg-rose-50"><ShieldX className="w-4 h-4" /></button>
                      ) : (
                        <button onClick={() => quickStatus(c, "pending")} title="Débloquer" className="p-2 border border-amber-300 text-amber-700 hover:bg-amber-50"><ShieldCheck className="w-4 h-4" /></button>
                      )}
                      <button onClick={() => setOffersClient(c)} title="Offres" className="p-2 border border-slate-300 hover:bg-slate-100"><FileText className="w-4 h-4" /></button>
                      <button onClick={() => { setEditClient(c); setFormOpen(true); }} title="Modifier" className="p-2 border border-slate-300 hover:bg-slate-100"><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => removeClient(c)} title="Supprimer" className="p-2 border border-rose-300 text-rose-600 hover:bg-rose-50"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>

      {typeChooserOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-3" onClick={() => setTypeChooserOpen(false)}>
          <div className="bg-white w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3 flex items-center justify-between border-b border-slate-200">
              <h3 className="font-semibold text-lg text-slate-800">Choisir le type de client</h3>
              <button type="button" onClick={() => setTypeChooserOpen(false)} className="text-slate-500 hover:text-slate-700"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <button type="button"
                onClick={() => { setNewClientType("particulier"); setTypeChooserOpen(false); setEditClient(null); setFormOpen(true); }}
                className="text-left p-5 border border-slate-200 hover:border-[#007EAE] hover:bg-[#007EAE]/5 transition-colors">
                <User className="w-8 h-8 text-[#007EAE]" />
                <p className="mt-3 text-lg font-semibold text-slate-800">Particulier</p>
                <p className="text-sm text-slate-500 mt-1">Crédit immobilier particulier</p>
              </button>
              <button type="button"
                onClick={() => { setNewClientType("sci"); setTypeChooserOpen(false); setEditClient(null); setFormOpen(true); }}
                className="text-left p-5 border border-slate-200 hover:border-[#007EAE] hover:bg-[#007EAE]/5 transition-colors">
                <Building2 className="w-8 h-8 text-[#007EAE]" />
                <p className="mt-3 text-lg font-semibold text-slate-800">Société (SCI)</p>
                <p className="text-sm text-slate-500 mt-1">Financement immobilier pour SCI</p>
              </button>
            </div>
          </div>
        </div>
      )}
      <ClientFormModal open={formOpen} onClose={() => setFormOpen(false)} onSaved={load} editClient={editClient} initialType={newClientType} />
      {offersClient && <OffersPanel client={offersClient} onClose={() => setOffersClient(null)} />}
      {resetRequestsOpen && <PasswordResetRequestsPanel onClose={() => setResetRequestsOpen(false)} />}
      <FooterCaixaBank />
    </div>
  );
};

export default AdminDashboard;
